  <script setup>
  import { ref, computed, onMounted } from "vue";
  import axios from "axios";
  import Swal from "sweetalert2";
  import { Preferences } from "@capacitor/preferences";
  import { useUserStore } from "../../stores/userStore";
  import router from "@/router";

  const userStore = useUserStore();

  const checklist = ref([
    { text: "He leído el aviso de privacidad", checked: false },
    { text: "Acepto el uso de mis datos para procesar pedidos", checked: false },
    { text: "Autorizo recibir notificaciones y promociones", checked: false },
  ]);

  const cliente = computed(() => userStore.user);
  const privacyPolicy = ref(null);

  const allChecked = computed(() =>
    checklist.value.every(item => item.checked)
  );

  const canAccept = computed(() => {
    if (!allChecked.value || !privacyPolicy.value || !cliente.value) return false;
    return (
      cliente.value.privacyPolicyVersion !== privacyPolicy.value.version
    );
  });

  const cargarPolitica = async () => {
    const { data } = await axios.get(
      "https://mvexpress.mx/api/privacy-policy"
    );
    privacyPolicy.value = data;
  };

  const cargarChecklist = async () => {
    const { value } = await Preferences.get({
      key: "avisoPrivacidadConfirmado",
    });
    if (value) checklist.value = JSON.parse(value);
  };

  const guardarChecklist = async () => {
    if (!canAccept.value) return;

    try {
      await Preferences.set({
        key: "avisoPrivacidadConfirmado",
        value: JSON.stringify(checklist.value),
      });

      await axios.post(
        "https://mvexpress.mx/api/aceptar-terminos",
        { userId: cliente.value.id },
        {
          headers: {
            Authorization: `Bearer ${userStore.token}`,
          },
        }
      );

      await userStore.setUser({
        ...cliente.value,
        politica_privacidad_aceptada: true,
        fecha_politica_privacidad: new Date().toISOString(),
        privacyPolicyVersion: privacyPolicy.value.version,
      });

      Swal.fire(
        "Éxito",
        "Aviso aceptado correctamente",
        "success"
      ).then(() => {
        router.replace("/home");
      });

    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo guardar la aceptación", "error");
    }
  };


  onMounted(async () => {
    await userStore.initSession();
    await cargarChecklist();
    await cargarPolitica();
  });

</script>

<template>
 
   
    
  <div class="main-content">

<v-container>
        <!-- Tarjeta principal -->
        <v-card>

          <!-- Sección Datos del Cliente -->
          <div class="bg-orange text-white px-6 py-4">
            <h2 class="text-xl font-semibold flex items-center gap-2">
              <v-icon color="white">mdi-account-circle</v-icon>
              Tus datos registrados
            </h2>
          </div>

          <div class="px-6 py-6 bg-white text-gray-700">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><strong>No de Cliente:</strong><br> {{ cliente?.cliente }}</div>
              <div><strong>Correo:</strong><br> {{ cliente?.email }}</div>
              <div><strong>Teléfono:</strong><br> {{ cliente?.telefono || "Sin dato" }}</div>
              <div><strong>Razón Social:</strong><br> {{ cliente?.razon }}</div>
              <div><strong>Comercial:</strong><br> {{ cliente?.denominacion_comercial }}</div>
              <div><strong>RFC:</strong><br> {{ cliente?.rfc }}</div>
              <div><strong>Ruta reparto:</strong><br> {{ cliente?.ruta_reparto }}</div>
              <div>
                <strong>Política aceptada:</strong><br>
                <v-chip :color="cliente?.politica_privacidad_aceptada ? 'green' : 'red'" class="mt-1" size="small">
                  {{ cliente?.politica_privacidad_aceptada ? 'Aceptada' : 'Pendiente' }}
                </v-chip>
              </div>
              <div>
                <strong>Fecha de aceptación:</strong><br>
                {{ cliente?.fecha_politica_privacidad
                  ? new Date(cliente.fecha_politica_privacidad).toLocaleDateString('es-MX')
                  : "Pendiente"
                }}
              </div>
            </div>
          </div>

          <!-- Contenido de la política con estilo profesional -->
          <div class="px-6 py-6 bg-gray-50 border-t">
            <h2 class="text-xl font-semibold text-gray-800 mb-3">
              {{ privacyPolicy?.title }}
            </h2>

            <p class="text-sm text-gray-500 mb-1">
              <strong>Versión:</strong> {{ privacyPolicy?.version }}
            </p>

            <div class="leading-relaxed text-gray-700 text-[15px] prose prose-slate max-w-none"
              style="line-height: 1.6; height: 320px; overflow-y: auto; text-align: justify; text-justify: inter-word;"
              v-html="privacyPolicy?.content" ref="scrollBox" @scroll="onScroll"></div>

            <p class="text-xs text-gray-500 mt-4">
              <strong>Última actualización:</strong>
              {{ new Date(privacyPolicy?.publishedAt).toLocaleDateString() }}
            </p>
          </div>

          <!-- Checklist -->
          <div v-if="cliente?.privacyPolicyVersion !== privacyPolicy?.version" class="px-6 py-6">
            <h2 class="text-lg font-semibold text-gray-800 mb-3">Confirmaciones requeridas</h2>

            <v-list class="rounded-xl border bg-white shadow-sm">
              <v-list-item v-for="(item, index) in checklist" :key="index">
                <v-checkbox v-model="item.checked" :label="item.text" color="indigo-darken-3" />

              </v-list-item>

            </v-list>
          </div>

          <!-- Botón aceptar -->
          <div class="text-center pb-8 px-6">
            <v-btn block color="indigo-darken-3" class="text-white py-4 text-lg font-semibold rounded-lg shadow-md"
              size="large" :disabled="!canAccept" @click="guardarChecklist">
              Aceptar y continuar
            </v-btn>


            <p v-if="cliente?.privacyPolicyVersion === privacyPolicy?.version" class="text-gray-500 text-sm mt-3">
              Ya aceptaste la versión más reciente.
            </p>
          </div>

        </v-card>
      </v-container>

</div>
      


</template>

<style>

.main-content {
  min-height: 100vh;
  padding-bottom: calc(env(safe-area-inset-bottom) + 220px) !important;
}
.snackbar-top {
  top: 30px !important;
  /* Ajusta la distancia del top */
  bottom: auto !important;
  z-index: 2000 !important;
}

.app-scroll {
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.marcas {
  display: flex;
  /* asegura que flex funcione */
  justify-content: center;
  /* centra elementos */
  overflow-x: auto;
  /* scroll horizontal */
  padding: 0 16px;
  /* margen interno */
  gap: 20px;
  /* espacio entre items */
  scroll-behavior: smooth;
  /* scroll suave */
}

.marcas::-webkit-scrollbar {
  display: none;
}

/* Cada item no se reduce */
.marcas .v-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* centra contenido dentro de la columna */
  padding: 0;
}

.fixed-cart-btn {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;

  /* SUBE el botón justo arriba de la bottom nav */
  bottom: calc(env(safe-area-inset-bottom) + 70px);

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;

  border-radius: 50px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  font-size: 16px;
  background-color: #fff;
}

.fixed-bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: env(safe-area-inset-bottom);
  /* respeta la zona segura del teléfono */
  z-index: 9999;
}

/* Para Samsung donde env(safe-area) no se reporta correctamente */
@supports (bottom: env(safe-area-inset-bottom)) {
  .fixed-cart-btn {
    bottom: calc(max(env(safe-area-inset-bottom), 20px) + 70px);
  }
}
</style>
